import styled from 'styled-components';

import Button from 'components/shared/Button';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { simpleDate } from 'lib/date';
import { DenialListItem, SEX_DISPLAY } from 'models/DenialListItem';
import { destroyAdminDenialListItem } from 'services/api/admin/denialListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, BodySmallBold } from 'styles/typography';

export type AdminDenialListDeleteConfirmProps = {
  denialListItem: DenialListItem;
  onClose: () => void;
};

// Inline delete confirmation rendered inside the admin Global Denial List slideout (the customer
// flow uses a centered modal; the admin mockup confirms in-panel). Shows the read-only patient
// details before a destructive soft-delete.
export default function AdminDenialListDeleteConfirm({ denialListItem, onClose }: AdminDenialListDeleteConfirmProps) {
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: destroyAdminDenialListItem,
  });

  const handleDelete = () => {
    mutate(
      { id: denialListItem.id },
      {
        onSuccess: () => {
          // Also invalidate the customer items cache so the client's Denial List page reflects the
          // admin's deletion (cross-surface consistency, per the ticket AC).
          invalidateQueries({ adminDenialListItems: true, adminDenialLists: true, denialListItems: true });
          addToast({ text: `${denialListItem.patientName} successfully removed from denial list` });
          onClose();
        },
      }
    );
  };

  return (
    <Container>
      <Title>Delete Patient?</Title>
      <Description>
        Are you sure you want to delete this patient&rsquo;s entry from the global denial list? This patient will no
        longer be flagged on matching referrals.
      </Description>

      <Details>
        <DetailRow label='Patient Name' value={denialListItem.patientName} />
        <DetailRow label='Date of Birth' value={simpleDate(denialListItem.dateOfBirth)} />
        <DetailRow label='Gender' value={SEX_DISPLAY[denialListItem.sex]} />
        <DetailRow label='Reason' value={denialListItem.reason} />
        <DetailRow label='Date Added' value={simpleDate(denialListItem.dateAdded)} />
      </Details>

      <Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='danger' onClick={handleDelete} loading={isPending} disabled={isPending}>
          Delete Patient
        </Button>
      </Actions>
    </Container>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <Detail>
      <BodySmallBold>{label}</BodySmallBold>
      <BodySmall>{value || <>&mdash;</>}</BodySmall>
    </Detail>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled(BodySmallBold)`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.accentRed};
  margin-bottom: 8px;
`;

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;
